import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface MultiSelectItemState {
  selected: number[];
  disabled: number[];
  attemptsCount: number;
  hasAttempted: boolean;
}

export type MultiSelectSliceState = Record<string, MultiSelectItemState>;

const getDefaultState = (): MultiSelectItemState => ({
  selected: [],
  disabled: [],
  attemptsCount: 0,
  hasAttempted: false,
});

const multiSelectSlice = createSlice({
  name: "mscq",
  initialState: {} as MultiSelectSliceState,
  reducers: {
    selectOption: (
      state,
      action: PayloadAction<{ componentID: string; optionID: number }>
    ) => {
      const { componentID, optionID } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      const selected = state[componentID].selected;
      if (selected.includes(optionID)) {
        state[componentID].selected = selected.filter((o) => o !== optionID);
      } else {
        selected.push(optionID);
      }
    },
    submit: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      const current = state[componentID] || getDefaultState();
      const attemptsCount = (current.attemptsCount || 0) + 1;
      const disabledList = [
        ...(current.disabled || []),
        ...(current.selected || []),
      ];
      state[componentID] = {
        disabled: disabledList,
        selected: current.selected || [],
        attemptsCount,
        hasAttempted: true,
      };
    },
    reset: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      state[componentID] = getDefaultState();
    },
    enableAll: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      state[componentID].disabled = [];
      state[componentID].selected = [];
    },
  },
  extraReducers: (builder) => {
    const isLegacy = (action: any, type: string) =>
      action.type === type && !action._fromTranslator;

    builder
      .addMatcher(
        (action) => isLegacy(action, "MSCQ/Select Option"),
        (state, action: any) => {
          const { componentID, optionID } = action.payload;
          if (!state[componentID]) {
            state[componentID] = getDefaultState();
          }
          const selected = state[componentID].selected;
          if (selected.includes(optionID)) {
            state[componentID].selected = selected.filter(
              (o) => o !== optionID
            );
          } else {
            selected.push(optionID);
          }
        }
      )
      .addMatcher(
        (action) => isLegacy(action, "MSCQ/Submit"),
        (state, action: any) => {
          const componentID = action.payload;
          const current = state[componentID] || getDefaultState();
          const attemptsCount = (current.attemptsCount || 0) + 1;
          const disabledList = [
            ...(current.disabled || []),
            ...(current.selected || []),
          ];
          state[componentID] = {
            disabled: disabledList,
            selected: current.selected || [],
            attemptsCount,
            hasAttempted: true,
          };
        }
      )
      .addMatcher(
        (action) => isLegacy(action, "MSCQ/Enable All"),
        (state, action: any) => {
          const componentID = action.payload;
          if (!state[componentID]) {
            state[componentID] = getDefaultState();
          }
          state[componentID].disabled = [];
          state[componentID].selected = [];
        }
      )
      .addMatcher(
        (action) => isLegacy(action, "MSCQ/Reset"),
        (state, action: any) => {
          const componentID = action.payload;
          state[componentID] = getDefaultState();
        }
      );
  },
});

export const multiSelectActions = multiSelectSlice.actions;
export const multiSelectReducer = multiSelectSlice.reducer;

export const selectMultiSelectState = (
  state: { mscq: MultiSelectSliceState },
  componentId: string
) => state.mscq?.[componentId] || getDefaultState();

/** @deprecated Use multiSelectActions */
export const mscqActions = multiSelectActions;
/** @deprecated Use multiSelectReducer */
export const mscqReducer = multiSelectReducer;
/** @deprecated Use selectMultiSelectState */
export const selectMSCQState = selectMultiSelectState;
/** @deprecated Use MultiSelectSliceState */
export type MSCQState = MultiSelectSliceState;
